import React, { useState, useEffect, useRef } from 'react';

const ProfilePopup = ({ 
    onAddReviews, 
    onUserProfile, 
    onLogout,
    className = '',
    isOpen = false,
    onClose = () => {}
}) => {
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };

        // Close menu on escape key
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    const handleMenuClick = (action) => {
        onClose();
        
        switch (action) {
            case 'addReviews':
                if (onAddReviews) onAddReviews();
                break;
            case 'userProfile':
                if (onUserProfile) onUserProfile();
                break;
            case 'logout':
                if (onLogout) onLogout();
                break;
            default:
                break;
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className={`absolute top-10 right-1 2xl:left-1 mt-2 bg-white border-1 border-roselea-background-gray rounded-md p-2.5 w-[154px] min-h-[136px] shadow-lg z-50 flex flex-col ${className}`}
            ref={menuRef}
        >
            {/* Add Reviews */}
            <button 
                className="flex justify-start items-center gap-1 px-2.5 py-3 cursor-pointer rounded-md transition-colors duration-200 ease-in-out text-gray-800 hover:bg-gray-50 focus:outline-none"
                onClick={() => handleMenuClick('addReviews')}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 6.67918L7.14 7.35906C7.25 7.42803 7.36 7.42567 7.47 7.35196C7.58 7.27826 7.62 7.17717 7.59 7.04868L7.29 5.76281L8.31 4.89079C8.41 4.80211 8.44 4.69628 8.4 4.57331C8.36 4.45034 8.27 4.38373 8.13 4.37349L6.795 4.27003L6.27 3.05806C6.22 2.93982 6.13 2.8807 6 2.8807C5.87 2.8807 5.78 2.93982 5.73 3.05806L5.205 4.27003L3.87 4.37349C3.73 4.38334 3.64 4.44995 3.6 4.57331C3.56 4.69668 3.59 4.8025 3.69 4.89079L4.71 5.76281L4.41 7.04868C4.38 7.17677 4.42 7.27787 4.53 7.35196C4.64 7.42606 4.75 7.42843 4.86 7.35906L6 6.67918ZM2.4 9.94557L1.02 11.3053C0.830001 11.4926 0.612401 11.5345 0.367201 11.4313C0.122001 11.328 -0.000399023 11.1432 9.77199e-07 10.8767V1.66873C9.77199e-07 1.34357 0.117601 1.06531 0.352801 0.833955C0.588001 0.602598 0.870401 0.486722 1.2 0.486328H10.8C11.13 0.486328 11.4126 0.602204 11.6478 0.833955C11.883 1.06571 12.0004 1.34397 12 1.66873V8.76317C12 9.08833 11.8826 9.36678 11.6478 9.59853C11.413 9.83029 11.1304 9.94596 10.8 9.94557H2.4ZM1.89 8.76317H10.8V1.66873H1.2V9.42827L1.89 8.76317Z" fill="#6A6868" fill-opacity="0.8"/>
                </svg>
                <span className="text-[15px] font-normal text-roselea-gray">Add Reviews</span>
            </button>
            
            {/* User Profile */}
            <button 
                className="flex justify-start items-center gap-2 px-2.5 py-3 cursor-pointer rounded-md transition-colors duration-200 ease-in-out text-gray-800 hover:bg-gray-50 focus:outline-none"
                onClick={() => handleMenuClick('userProfile')}
            >
                <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.18163 5.34979C8.18163 5.92844 7.95176 6.4834 7.54259 6.89257C7.13342 7.30174 6.57847 7.53161 5.99981 7.53161C5.42116 7.53161 4.8662 7.30174 4.45703 6.89257C4.04786 6.4834 3.81799 5.92844 3.81799 5.34979C3.81799 4.77113 4.04786 4.21618 4.45703 3.80701C4.8662 3.39784 5.42116 3.16797 5.99981 3.16797C6.57847 3.16797 7.13342 3.39784 7.54259 3.80701C7.95176 4.21618 8.18163 4.77113 8.18163 5.34979ZM7.09072 5.34979C7.09072 5.63911 6.97579 5.91659 6.7712 6.12118C6.56662 6.32576 6.28914 6.4407 5.99981 6.4407C5.71048 6.4407 5.43301 6.32576 5.22842 6.12118C5.02384 5.91659 4.9089 5.63911 4.9089 5.34979C4.9089 5.06046 5.02384 4.78298 5.22842 4.5784C5.43301 4.37381 5.71048 4.25888 5.99981 4.25888C6.28914 4.25888 6.56662 4.37381 6.7712 4.5784C6.97579 4.78298 7.09072 5.06046 7.09072 5.34979Z" fill="#6A6868" fill-opacity="0.8"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6 0.986328C2.68636 0.986328 0 3.67269 0 6.98633C0 10.3 2.68636 12.9863 6 12.9863C9.31364 12.9863 12 10.3 12 6.98633C12 3.67269 9.31364 0.986328 6 0.986328ZM1.09091 6.98633C1.09091 8.12633 1.47982 9.17578 2.13164 10.0092C2.58951 9.40822 3.18008 8.92113 3.85726 8.586C4.53443 8.25087 5.27989 8.07675 6.03545 8.07724C6.7813 8.07637 7.51749 8.24583 8.18789 8.57271C8.85829 8.89959 9.44518 9.37526 9.90382 9.96342C10.3764 9.34357 10.6946 8.62008 10.8321 7.85283C10.9696 7.08558 10.9224 6.29662 10.6945 5.55123C10.4665 4.80584 10.0643 4.12545 9.52117 3.56635C8.97805 3.00725 8.30959 2.58552 7.57112 2.33606C6.83264 2.0866 6.04538 2.01657 5.27447 2.13177C4.50356 2.24697 3.77116 2.54409 3.13787 2.99854C2.50459 3.45299 1.98862 4.05171 1.63267 4.74516C1.27672 5.43861 1.09101 6.20686 1.09091 6.98633ZM6 11.8954C4.87304 11.8972 3.78008 11.5095 2.90618 10.798C3.2579 10.2943 3.72611 9.88308 4.27095 9.59931C4.81579 9.31553 5.42114 9.16762 6.03545 9.16815C6.6421 9.16762 7.24011 9.31185 7.77981 9.58886C8.31951 9.86588 8.78532 10.2677 9.13855 10.7609C8.25789 11.4956 7.1469 11.8972 6 11.8954Z" fill="#6A6868" fill-opacity="0.8"/>
                </svg>
                <span className="text-[15px] font-normal text-roselea-gray">User Profile</span>
            </button>
            
            {/* Logout */}
            <button 
                className="flex justify-start items-center gap-2 px-2.5 py-3 cursor-pointer rounded-md transition-colors duration-200 ease-in-out text-gray-800 hover:bg-gray-50 focus:outline-none"
                onClick={() => handleMenuClick('logout')}
            >
                <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.20837 2.27799L7.44462 3.04174L8.84212 4.44466H3.33337V5.528H8.84212L7.44462 6.9255L8.20837 7.69466L10.9167 4.98633L8.20837 2.27799ZM1.16671 1.19466H5.50004V0.111328H1.16671C0.570874 0.111328 0.083374 0.598828 0.083374 1.19466V8.778C0.083374 9.37383 0.570874 9.86133 1.16671 9.86133H5.50004V8.778H1.16671V1.19466Z" fill="#888686"/>
                </svg>
                <span className="text-[15px] font-normal text-roselea-gray">Logout</span>
            </button>
        </div>
    );
};

export default ProfilePopup; 